import { Coin } from "@keplr-wallet/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Connection } from "@solana/web3.js";
import { BaseWalletAdapter, WalletReadyState } from "@solana/wallet-adapter-base";
import { SolanaMobileWalletAdapterWalletName } from "@solana-mobile/wallet-adapter-mobile";
import { sendLogError } from "@axvdex/utils/rpcClientQuerySmartContractWrapper";
import { authorizationResultCache } from "@axvdex/utils/solanaWallet";
import { clearWallet, initWalletInfo, setLoadingWallet, updateConnectionStatus } from "state/wallet/walletSlice";
import { loadState, persistState, removeState, WHITE_LIST_PERSISTED_STATE_KEYS } from "state/persist";
import { AsyncThunkConfig } from "state";

export interface Wallet {
  name: string;
  address: string;
  connectAccountNumber: number;
  connectSequence: number;
  signerType: string;
}

let connectingWallet = false;

export const connectSolanaWallet = async (walletAdapter: BaseWalletAdapter) => {
  if (connectingWallet) throw Error("Already connecting wallet...");
  connectingWallet = true;
  try {
    const chainId = process.env.REACT_APP_ARCHWAY_NETWORK as string;
    const coinDenom = process.env.REACT_APP_ARCHWAY_DENOM as string;
    const coinMinimalDenom = process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM as string;
    const chainName = process.env.REACT_APP_ARCHWAY_NETWORK as string;
    const rpc = process.env.REACT_APP_RPC_URL as string;
    const rest = process.env.REACT_APP_REST_URL as string;

    //if any of the env variables are missing, don't connect
    if (!chainId || !coinDenom || !coinMinimalDenom || !chainName || !rpc || !rest) {
      console.error("Missing environment variables");
      throw new Error("Missing environment variables");
    }

    // Initialize wallet adapter
    const connectionRes = await walletAdapter.connect();
    console.log("connectionRes", connectionRes);
    if (!walletAdapter.connected) {
      throw new Error("Not able to connect wallet");
    }

    if (walletAdapter.name === SolanaMobileWalletAdapterWalletName) {
      console.log("walletAdapter", walletAdapter);
      const cached = await authorizationResultCache.get();
      if (cached && cached.accounts && cached.accounts[0].label) {
        // HACK: [Android only] Pass the wallet name to the view
        // - The wallet name is not provided on that platform
        // - We can't overwrite the property because it's use
        //   internally to detect the fact that it's the mobile wallet
        // - Grep [Android only] to see other place it's used
        window.walletName = cached.accounts[0].label;
        walletAdapter.icon = cached.accounts[0].icon;
      } else {
        window.walletName = "Your Wallet";
        walletAdapter.icon = undefined;
      }
    }

    // Build an equivalent of the `window.client` used by cosmo
    window.solanaClient = walletAdapter;

    // Extract some wallet info
    const publicKey = walletAdapter.publicKey;
    const walletNativeBalances: Coin[] = [];
    const connection = new Connection(process.env.REACT_APP_SOLANA_NETWORK);
    const accountBalance = await connection.getBalance(publicKey);
    walletNativeBalances.push({ denom: coinDenom, amount: accountBalance.toString() });
    const wallet: Wallet = {
      name: walletAdapter.name,
      address: publicKey.toBase58(),
      connectAccountNumber: null,
      connectSequence: null,
      signerType: "solana",
    };

    connectingWallet = false;
    return { wallet, walletNativeBalances };
  } catch (e) {
    connectingWallet = false;
    sendLogError(e.message);
    console.error(e);
    throw e;
  }
};

export const disconnectWallet = createAsyncThunk<void, void, AsyncThunkConfig>(
  "wallet/disconnectWallet",
  async (_, { getState, dispatch }) => {
    const walletExtension: string = loadState(WHITE_LIST_PERSISTED_STATE_KEYS.connectedWallet);
    // remove items from localstorage (permit)
    const walletInfo = getState().wallet.walletInfo;
    localStorage.removeItem("connected");
    const permits = loadState(WHITE_LIST_PERSISTED_STATE_KEYS.permits);
    delete permits[process.env.REACT_APP_ARCHWAY_NETWORK + "_" + walletInfo.walletAddress];
    persistState(WHITE_LIST_PERSISTED_STATE_KEYS.permits, permits);
    persistState(WHITE_LIST_PERSISTED_STATE_KEYS.connectedWallet, "");
    persistState(WHITE_LIST_PERSISTED_STATE_KEYS.autoConnectWallet, false);
    persistState(WHITE_LIST_PERSISTED_STATE_KEYS.agreedCompliance, false);
    removeState(WHITE_LIST_PERSISTED_STATE_KEYS.dashboardGrid);
    if (!/undefined/i.test(typeof window[walletExtension].disable)) {
      await window[walletExtension].disable();
    } else if (!/undefined/i.test(typeof window[walletExtension].disconnect)) {
      await window.solana.disconnect();
    }

    // erase state
    dispatch(clearWallet());
    window.solanaClient = null;
  }
);

export const connectSolanaWalletWithDispatch = createAsyncThunk<
  Wallet | null,
  { walletAdapter: BaseWalletAdapter },
  AsyncThunkConfig
>("wallet/connectWalletWithDispatch", async ({ walletAdapter }, { dispatch }) => {
  try {
    dispatch(setLoadingWallet(true));
    if (walletAdapter.readyState !== WalletReadyState.Installed) {
      if (walletAdapter.name === SolanaMobileWalletAdapterWalletName) {
        // Need the authorization result to be able to connect
        console.debug("[Mobile wallet] Asking authorization to connect ...");
        await walletAdapter.connect();
      } else {
        throw new Error(`Trying to connect with a uninstalled wallet. Current state: ${walletAdapter.readyState}`);
      }
    }
    const { wallet, walletNativeBalances } = await connectSolanaWallet(walletAdapter);
    dispatch(
      initWalletInfo({
        wallet,
        assetBalances: walletNativeBalances.reduce(
          (obj, item) => Object.assign(obj, { [item.denom]: item.amount }),
          {}
        ),
        useSolanaAdapter: true,
      })
    );
    persistState(WHITE_LIST_PERSISTED_STATE_KEYS.autoConnectWallet, true);
    return wallet;
  } catch (e) {
    console.error("Error connecting wallet: ", e);
    if ("Already connecting wallet..." === e.message) return null;
    if ("UNINSTALLED" === e.message) {
      // dispatch(
      //   sendToast({ type: "tx-fail", info: { msg: "Wallet not found!", toastID: "" + new Date().getTime() } })
      // );
    }
    persistState(WHITE_LIST_PERSISTED_STATE_KEYS.autoConnectWallet, false);
    persistState(WHITE_LIST_PERSISTED_STATE_KEYS.connectedWallet, "");
    dispatch(
      updateConnectionStatus({
        isConnected: false,
        walletAddress: "",
        assetBalances: {
          [process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM || ""]: 0,
        },
        connectionStatus:
          "No wallet exists" === e.message
            ? "No wallet exists, please create one..."
            : "UNINSTALLED" === e.message
              ? "Install keplr extension..."
              : "Invalid chain Id" === e.message
                ? "NOT_CONNECTED"
                : "NOT_CONNECTED",
      })
    );
    return null;
  }
});

// export const createUserPermit = async (walletAddress: string) => {
//   const walletExtension: string = loadState(WHITE_LIST_PERSISTED_STATE_KEYS.connectedWallet);
//   let walletExtensionClient = window[walletExtension];
//   if (!walletExtensionClient?.signAmino) {
//     throw new Error("Please install wallet extension");
//   }
//   const { signature } = await walletExtensionClient.signAmino(
//     process.env.REACT_APP_ARCHWAY_NETWORK || "",
//     walletAddress,
//     {
//       account_number: "0", // Must be 0
//       chain_id: process.env.REACT_APP_ARCHWAY_NETWORK || "",
//       fee: {
//         amount: [{ denom: process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM || "", amount: "0" }],
//         gas: "0",
//       },
//       memo: "", // Must be empty
//       msgs: [
//         {
//           type: "permit",
//           value: "Astrovault User Permit",
//         },
//       ],
//       sequence: "0", // Must be 0
//     },
//     {
//       preferNoSetFee: true, // Fee must be 0, so hide it from the user
//       preferNoSetMemo: true, // Memo must be empty, so hide it from the user
//     }
//   );
//
//   return signature;
// };
//
